package com.snakesworld.game.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.ui.theme.LocalGameCanvasColors
import com.snakesworld.game.ui.theme.appFontFamily
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.block_player
import snakesworld.shared.generated.resources.cancel
import snakesworld.shared.generated.resources.confirm_action
import snakesworld.shared.generated.resources.confirm_block_desc
import snakesworld.shared.generated.resources.confirm_block_title
import snakesworld.shared.generated.resources.confirm_report_desc
import snakesworld.shared.generated.resources.confirm_report_title
import snakesworld.shared.generated.resources.moderate_player_desc
import snakesworld.shared.generated.resources.moderate_player_title
import snakesworld.shared.generated.resources.report_player
import snakesworld.shared.generated.resources.report_sent

private enum class ModerationStep {
    Actions,
    ConfirmReport,
    ConfirmBlock,
    ReportSent,
}

/**
 * Neon dialog for one other player's nickname. Report and Block each ask for
 * confirmation before the existing repository calls run.
 */
@Composable
fun PlayerModerationDialog(
    playerId: String?,
    rawName: String?,
    displayName: String,
    onReport: (id: String, name: String) -> Unit,
    onBlock: (id: String) -> Unit,
    onDismiss: () -> Unit,
) {
    val canvasColors = LocalGameCanvasColors.current
    var step by remember(playerId) { mutableStateOf(ModerationStep.Actions) }
    val visible = playerId != null && rawName != null
    val title = when (step) {
        ModerationStep.ConfirmReport -> stringResource(Res.string.confirm_report_title)
        ModerationStep.ConfirmBlock -> stringResource(Res.string.confirm_block_title)
        ModerationStep.ReportSent -> stringResource(Res.string.report_sent)
        ModerationStep.Actions -> stringResource(Res.string.moderate_player_title)
    }
    val body = when (step) {
        ModerationStep.ConfirmReport -> stringResource(Res.string.confirm_report_desc)
        ModerationStep.ConfirmBlock -> stringResource(Res.string.confirm_block_desc)
        ModerationStep.ReportSent -> stringResource(Res.string.report_sent)
        ModerationStep.Actions -> stringResource(Res.string.moderate_player_desc)
    }
    ArcadeDialog(
        visible = visible,
        onDismissRequest = {
            if (step == ModerationStep.ConfirmReport || step == ModerationStep.ConfirmBlock) {
                step = ModerationStep.Actions
            } else {
                onDismiss()
            }
        },
        title = {
            Text(
                text = title,
                fontFamily = appFontFamily(),
                fontWeight = FontWeight.Black,
                color = if (step == ModerationStep.ConfirmBlock) canvasColors.dangerColor else canvasColors.accentColor,
                fontSize = 16.sp,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                EllipsizedNickname(
                    text = displayName,
                    color = canvasColors.hudTextColor,
                    fontSize = 14.sp,
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Start,
                )
                Text(
                    text = body,
                    fontFamily = appFontFamily(),
                    color = canvasColors.hudTextColor,
                    fontSize = 12.sp,
                )
            }
        },
        confirmButton = {
            when (step) {
                ModerationStep.Actions -> {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Button(
                            onClick = { step = ModerationStep.ConfirmReport },
                            colors = ButtonDefaults.buttonColors(
                                containerColor = canvasColors.hudBgColor,
                                contentColor = canvasColors.hudTextColor,
                            ),
                            shape = RoundedCornerShape(6.dp),
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            Text(
                                text = stringResource(Res.string.report_player),
                                fontFamily = appFontFamily(),
                                fontWeight = FontWeight.Bold,
                                maxLines = 1,
                                softWrap = false,
                                overflow = TextOverflow.Ellipsis,
                            )
                        }
                        Button(
                            onClick = { step = ModerationStep.ConfirmBlock },
                            colors = ButtonDefaults.buttonColors(
                                containerColor = canvasColors.dangerColor,
                                contentColor = canvasColors.hudTextColor,
                            ),
                            shape = RoundedCornerShape(6.dp),
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            Text(
                                text = stringResource(Res.string.block_player),
                                fontFamily = appFontFamily(),
                                fontWeight = FontWeight.Bold,
                                maxLines = 1,
                                softWrap = false,
                                overflow = TextOverflow.Ellipsis,
                            )
                        }
                    }
                }
                ModerationStep.ConfirmReport -> {
                    Button(
                        onClick = {
                            val id = playerId ?: return@Button
                            onReport(id, rawName.orEmpty())
                            step = ModerationStep.ReportSent
                        },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = canvasColors.accentColor,
                            contentColor = canvasColors.hudBgColor,
                        ),
                        shape = RoundedCornerShape(6.dp),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text(
                            text = stringResource(Res.string.confirm_action),
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                        )
                    }
                }
                ModerationStep.ConfirmBlock -> {
                    Button(
                        onClick = {
                            val id = playerId ?: return@Button
                            onBlock(id)
                            onDismiss()
                        },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = canvasColors.dangerColor,
                            contentColor = canvasColors.hudTextColor,
                        ),
                        shape = RoundedCornerShape(6.dp),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text(
                            text = stringResource(Res.string.confirm_action),
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                        )
                    }
                }
                ModerationStep.ReportSent -> {
                    Button(
                        onClick = onDismiss,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = canvasColors.hudTextColor,
                            contentColor = canvasColors.hudBgColor,
                        ),
                        shape = RoundedCornerShape(6.dp),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text(
                            text = stringResource(Res.string.report_sent),
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                        )
                    }
                }
            }
        },
        dismissButton = {
            if (step != ModerationStep.ReportSent) {
                TextButton(
                    onClick = {
                        if (step == ModerationStep.Actions) onDismiss() else step = ModerationStep.Actions
                    },
                ) {
                    Text(
                        text = stringResource(Res.string.cancel),
                        fontFamily = appFontFamily(),
                        color = canvasColors.hudTextColor,
                        maxLines = 1,
                    )
                }
            }
        },
        borderColor = if (step == ModerationStep.ConfirmBlock) canvasColors.dangerColor else canvasColors.accentColor,
        canvasColors = canvasColors,
    )
}
